import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ThemeProvider } from "@webtemplate/ui";
import { trpc, createTRPCClient } from "@/core/api";
import { PluginTrpcProvider } from "@/core/api/plugin-trpc-provider";
import { AuthProvider, useAuth } from "@/core/auth";
import { TenantBrandingProvider } from "@/core/branding";
import { ToastProvider } from "@/core/components/toast";
import { FeatureFlagProvider } from "@/core/plugins/feature-flags/feature-flag-context";
import { PluginProvider } from "@/core/plugins/plugin-context";
import { clientPluginManifests } from "@/core/plugins/client-plugin-registry";
import { getPluginFlagKeys } from "@/core/plugins/plugin-registry";
import { createAppRouter } from "./router";
import { LoadingFallback } from "./Root";
import type { RouterContext } from "./router.types";

const pluginFlagKeys = getPluginFlagKeys(clientPluginManifests);

const queryClient = new QueryClient();
const trpcClient = createTRPCClient();

/**
 * Mounts the router. Constructed in a CHILD component that only renders once the
 * initial session restore has resolved (initialCheckDone) so the router is born
 * with the RESTORED auth context.
 *
 * This is load-bearing for auth-persistence-on-refresh (D-03 / triage cause C3):
 * TanStack Router runs its INITIAL location load -- including the authenticated
 * route's beforeLoad guard -- against the context the router was CONSTRUCTED
 * with. If the router is created during the first render (status=idle ->
 * isAuthenticated=false) and only context-updated later, a page reload restores
 * the session asynchronously (refresh 200 + me 200) yet the already-resolved
 * initial match has already redirected to /login against the stale unauthenticated
 * context. Constructing the router here -- after the latch opens, with the live
 * authenticated context -- means the initial guard sees the true auth state and
 * the session survives the reload.
 */
function RouterHost({ routerContext }: { routerContext: RouterContext }) {
  // useState initializer captures the context AT MOUNT. Because this component
  // only mounts after initialCheckDone, that context is the post-restore one.
  const [router] = useState(() => createAppRouter(routerContext));

  // Keep the router context in sync for all SUBSEQUENT auth transitions (login,
  // logout, role changes). useLayoutEffect runs before paint so navigate() never
  // sees stale context.
  useLayoutEffect(() => {
    router.update({ context: routerContext });
  }, [router, routerContext]);

  return <RouterProvider router={router} />;
}

function AppRouter() {
  const auth = useAuth();
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // Latch the initial session restore as complete once auth status first
  // resolves from idle/loading. After this, the router stays mounted through
  // all subsequent loading transitions (login, register, etc.) so forms don't
  // unmount mid-submission. Setting state during render (guarded so it runs
  // once) is React's recommended alternative to a setState-in-effect.
  if (!initialCheckDone && auth.status !== "idle" && auth.status !== "loading") {
    setInitialCheckDone(true);
  }

  // Safety timeout: if the auth restore hangs (backend down, proxy timeout,
  // slow endpoint), render the router anyway after 5 seconds. The user will
  // land on the login page and can authenticate once the backend recovers.
  useEffect(() => {
    if (initialCheckDone) return;
    const timeout = setTimeout(() => setInitialCheckDone(true), 5_000);
    return () => clearTimeout(timeout);
  }, [initialCheckDone]);

  const routerContext: RouterContext = useMemo(
    () => ({
      auth: {
        isAuthenticated: auth.isAuthenticated,
        isAdmin: auth.isAdmin,
        isSuperAdmin: auth.isSuperAdmin,
      },
    }),
    [auth.isAuthenticated, auth.isAdmin, auth.isSuperAdmin],
  );

  if (!initialCheckDone) {
    return <LoadingFallback />;
  }

  return <RouterHost routerContext={routerContext} />;
}

export function App() {
  return (
    // ThemeProvider (hoisted into @webtemplate/ui, D-10 / 08.9-01) is mounted
    // app-wide here — above the whole surface (auth, showcase, admin) — so
    // switching theme/mode re-skins EVERYTHING, not just the settings panel
    // (which used to wrap a local ThemeProvider). The no-FOUC pre-paint script in
    // index.html already applied the .dark class before React mounts; this
    // provider hydrates from the same `theme` cookie so there is no flash.
    <ThemeProvider>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <PluginTrpcProvider>
            <FeatureFlagProvider flagKeys={pluginFlagKeys}>
              <PluginProvider plugins={clientPluginManifests}>
                <ToastProvider>
                  <AuthProvider>
                    <TenantBrandingProvider>
                      <AppRouter />
                    </TenantBrandingProvider>
                  </AuthProvider>
                </ToastProvider>
              </PluginProvider>
            </FeatureFlagProvider>
          </PluginTrpcProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </ThemeProvider>
  );
}
